import { AuthUserPermission, FieldModel } from '@cibo/core'
import { Can } from '@cibo/profile'
import { usePrograms } from '@cibo/programs'
import { CoverCropChip, CropChip, TillageChip } from '@cibo/ui'
import {
  Box,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import { flatten, uniq } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const StyledTableCell = styled(TableCell)(() => ({
  whiteSpace: 'nowrap',
}))

export const FieldTablePanelContent = ({ field }: { field: FieldModel }) => {
  const programs = usePrograms()
  const reportYears = useMemo(() => {
    if (programs.isFetched) {
      return uniq(
        flatten([
          programs.data?.map(p =>
            p.program.benchmarks?.find(b => b.benchmark === 'enrollment')?.details.map(d => d.year)
          ) || [],
          field.details.map(({ year }) => year),
        ])
      )
        .sort()
        .reverse()
        .filter(y => !!y)
    }
    return []
  }, [programs.isFetched])

  const { t } = useTranslation('@cibo/impact/FieldTablePanelContent')

  return (
    <Stack spacing={2}>
      <Stack paddingX={2} paddingY={4} spacing={4} direction="row">
        <Box minWidth={700}>
          <TableContainer
            sx={{ borderRadius: 1, backgroundColor: 'background.paper', overflow: 'hidden' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={5}>{t('ownership')}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableRow>
                <StyledTableCell>
                  {!!field.resolveStandingDetail('ownership')?.input
                    ? field.resolveStandingDetail('ownership')?.input?.operatorIsOwner
                      ? `Owned`
                      : `Rented`
                    : '--'}
                </StyledTableCell>
                <StyledTableCell colSpan={4}>
                  {!!field.resolveStandingDetail('ownership')?.input
                    ? field.resolveStandingDetail('ownership')?.input?.operatorIsOwner
                      ? `${field.resolveStandingDetail('ownership')?.input?.yearsInOperation} years`
                      : `${field.resolveStandingDetail('ownership')?.input?.yearsInOperation} years`
                    : '--'}
                </StyledTableCell>
              </TableRow>
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t('year')}</StyledTableCell>
                  <StyledTableCell>{t('crop')}</StyledTableCell>
                  <StyledTableCell>{t('Cover Crop', { ns: 'clientLocalization' })}</StyledTableCell>
                  <StyledTableCell>{t('tillage')}</StyledTableCell>
                  <StyledTableCell>{t('irrigation')}</StyledTableCell>
                </TableRow>
              </TableHead>
              {reportYears.map(year => {
                const cashCrop = field.cashCrops.find(c => c.year === year)
                const coverCrop = field.coverCrops.find(c => c.year === year)
                const tillage = field.tillageEvents.find(c => c.year === year)
                const irrigation = field.irrigationEvents.find(c => c.year === year)
                return (
                  <TableRow key={year}>
                    <StyledTableCell>{year}</StyledTableCell>
                    <StyledTableCell>
                      <Stack>
                        <Box>
                          {!!cashCrop?.input?.crop ? (
                            <CropChip crop={cashCrop?.input?.crop} source={cashCrop.source} />
                          ) : cashCrop?.result !== undefined ? (
                            <CropChip crop={cashCrop.result} source={cashCrop.source} />
                          ) : (
                            '--'
                          )}
                        </Box>
                      </Stack>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        {!!coverCrop?.input?.crop ? (
                          <CoverCropChip crop={coverCrop?.input?.crop} source={coverCrop.source} />
                        ) : coverCrop?.result !== undefined ? (
                          <CoverCropChip crop={coverCrop.result} source={coverCrop.source} />
                        ) : (
                          '--'
                        )}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TillageChip type={tillage?.result} source={tillage?.source} />
                    </StyledTableCell>
                    <StyledTableCell>
                      {!!irrigation?.result !== undefined
                        ? t(irrigation?.result ? 'irrigated' : 'notIrrigated')
                        : '--'}
                    </StyledTableCell>
                  </TableRow>
                )
              })}
            </Table>
          </TableContainer>
          {/* <Button variant="outlined">Add Information</Button> */}
        </Box>
      </Stack>
      <Can useAny={AuthUserPermission.DEBUG_USER}>
        <Stack padding={2} spacing={2}>
          DEBUG
          <Stack direction="row" spacing={2}>
            <TableContainer
              sx={{ borderRadius: 1, backgroundColor: 'background.paper', overflow: 'hidden' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Program</StyledTableCell>
                    <StyledTableCell>
                      <pre>availableForWorkflow</pre>
                    </StyledTableCell>
                    <StyledTableCell>
                      <pre>workflow</pre>
                    </StyledTableCell>
                    <StyledTableCell>
                      <pre>perAcrePotential</pre>
                    </StyledTableCell>
                    <StyledTableCell>Profile Status</StyledTableCell>
                    <StyledTableCell>Enrollment Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                {field.programStatuses?.map(p => (
                  <TableRow key={p.programId}>
                    <StyledTableCell>{p.programId}</StyledTableCell>
                    <StyledTableCell>{p.availableForWorkflow}</StyledTableCell>
                    <StyledTableCell>{p.workflow}</StyledTableCell>
                    <StyledTableCell>
                      <pre>
                        {JSON.stringify(field.programPerAcrePotential(p.programId), null, ' ')}
                      </pre>
                    </StyledTableCell>
                    <StyledTableCell>
                      {p.benchmarks.find(b => b.benchmark === 'profile')?.status}
                    </StyledTableCell>
                    <StyledTableCell>
                      {p.benchmarks.find(b => b.benchmark === 'enrollment')?.status}
                    </StyledTableCell>
                  </TableRow>
                ))}
              </Table>
            </TableContainer>

            <Box maxHeight={400} overflow="auto">
              <pre>{JSON.stringify(field, null, '  ')}</pre>
            </Box>
          </Stack>
        </Stack>
      </Can>
    </Stack>
  )
}
